import ActivityMetrics from "./ActivityMetrics";
import dayjs, { Dayjs } from "dayjs";
import type {
  MeetingCancellationReason,
  TeamDismissalReason,
  TeamMarkedDoneChannel,
  TeamMeetingState,
} from "@acto/gql/graphql";

interface MeetingStateCountsFragment {
  planned: number;
  documented: number;
  noReport: number;
  didNotTakePlace: number;
}

interface ActivityMetricsFragment {
  meetingsCount: number;
  recapsCount: number;
  dismissalsCount: number;
  openTodosCount: number;
  meetingStates?: MeetingStateCountsFragment | null;
}

interface EmployeeKpiValueFragment {
  totalValue: number;
  previousTotalValue?: number | null;
  percentageChange?: number | null;
}

/**
 * The `client: ClientInfo` selection of a team-scoped record: the `Client` typename marks a
 * client the viewer can open, `RestrictedClient` one they may only identify.
 */
interface ClientInfoFragment {
  __typename: string;
  id: string;
  name: string;
  externalReference?: string | null;
}

const isAccessibleClient = (client: ClientInfoFragment | null | undefined): boolean => client?.__typename === "Client";

interface MeetingNodeFragment {
  id: string;
  scheduledDate: string;
  client?: ClientInfoFragment | null;
  note?: {
    id: string;
    summary?: string | null;
    content: string;
    openTodosCount: number;
  } | null;
  state?: TeamMeetingState | null;
  cancellation?: {
    reason: MeetingCancellationReason;
    comment?: string | null;
  } | null;
  observation?: { id: string } | null;
  series?: { id: string } | null;
}

interface MeetingEdgeFragment {
  node: MeetingNodeFragment;
}

export interface MeetingCancellationSummary {
  reason: MeetingCancellationReason;
  comment: string | null;
}

export class MeetingSummary {
  constructor(
    public readonly id: string,
    public readonly scheduledDate: Dayjs,
    /** Null when the client record no longer exists. */
    public readonly clientId: string | null,
    public readonly clientName: string | null,
    public readonly clientExternalReference: string | null,
    /** False when the client is outside the viewer's own scope. */
    public readonly clientAccessible: boolean,
    public readonly hasNote: boolean,
    public readonly noteId: string | null = null,
    public readonly noteSummary: string | null = null,
    public readonly noteContent: string | null = null,
    public readonly openTodosCount = 0,
    public readonly state: TeamMeetingState | null = null,
    public readonly cancellation: MeetingCancellationSummary | null = null,
    /** The Acto signal this meeting came from; null for a meeting the rep set up without one. */
    public readonly observationId: string | null = null,
    /** The recurring series this meeting is an occurrence of; null for a one-off meeting. */
    public readonly seriesId: string | null = null,
  ) {}

  static fromFragment(data: MeetingNodeFragment): MeetingSummary {
    return new MeetingSummary(
      data.id,
      dayjs(data.scheduledDate),
      data.client?.id ?? null,
      data.client?.name ?? null,
      data.client?.externalReference ?? null,
      isAccessibleClient(data.client),
      !!data.note,
      data.note?.id ?? null,
      data.note?.summary ?? null,
      data.note?.content ?? null,
      data.note?.openTodosCount ?? 0,
      data.state ?? null,
      data.cancellation ? { reason: data.cancellation.reason, comment: data.cancellation.comment ?? null } : null,
      data.observation?.id ?? null,
      data.series?.id ?? null,
    );
  }

  get description(): string {
    if (this.noteSummary) return this.noteSummary;
    if (this.noteContent) return this.noteContent.slice(0, 200);
    return "";
  }
}

interface DismissalNodeFragment {
  id: string;
  dismissedAt: string;
  dismissedUntil: string;
  client?:
    | (ClientInfoFragment & {
        city?: string | null;
        postcode?: string | null;
        industry?: string | null;
        todaysRecommendedObservation?: {
          title?: { locale: string; value: string }[] | null;
        } | null;
        lastVisitDate?: string | null;
      })
    | null;
  answer?: {
    reason?: TeamDismissalReason | null;
    comment?: string | null;
  } | null;
}

interface DismissalEdgeFragment {
  node: DismissalNodeFragment;
}

interface MarkedDoneNodeFragment {
  id: string;
  markedDoneAt: string;
  channel?: TeamMarkedDoneChannel | null;
  client?: ClientInfoFragment | null;
  report?: {
    id: string;
    summary?: string | null;
    content: string;
  } | null;
}

interface MarkedDoneEdgeFragment {
  node: MarkedDoneNodeFragment;
}

interface TodoNodeFragment {
  id: string;
  title?: string | null;
  description?: string | null;
  dueDate?: string | null;
  createdAt?: string | null;
  client?: ClientInfoFragment | null;
}

export interface DismissalAnswer {
  reason: TeamDismissalReason | null;
  comment: string | null;
}

export class DismissalSummary {
  constructor(
    public readonly id: string,
    public readonly dismissedAt: Dayjs,
    public readonly dismissedUntil: Dayjs,
    /** Null when the client record no longer exists. */
    public readonly clientId: string | null,
    public readonly clientName: string | null,
    public readonly clientExternalReference: string | null,
    /** False when the client is outside the viewer's own scope. */
    public readonly clientAccessible: boolean,
    public readonly clientCity: string | null,
    public readonly clientPostcode: string | null,
    public readonly clientIndustry: string | null,
    public readonly observationTitle: string | null,
    public readonly lastVisitDate: Dayjs | null,
    public readonly answer: DismissalAnswer | null = null,
  ) {}

  static fromFragment(data: DismissalNodeFragment): DismissalSummary {
    const client = data.client;
    const observationTitle = client?.todaysRecommendedObservation?.title?.[0]?.value ?? null;
    const lastVisitDate = client?.lastVisitDate ? dayjs(client.lastVisitDate) : null;

    return new DismissalSummary(
      data.id,
      dayjs(data.dismissedAt),
      dayjs(data.dismissedUntil),
      client?.id ?? null,
      client?.name ?? null,
      client?.externalReference ?? null,
      isAccessibleClient(client),
      client?.city ?? null,
      client?.postcode ?? null,
      client?.industry ?? null,
      observationTitle,
      lastVisitDate,
      data.answer ? { reason: data.answer.reason ?? null, comment: data.answer.comment ?? null } : null,
    );
  }
}

export class MarkedDoneSummary {
  constructor(
    public readonly id: string,
    public readonly markedDoneAt: Dayjs,
    public readonly clientId: string | null,
    public readonly clientName: string | null,
    public readonly clientExternalReference: string | null,
    public readonly clientAccessible: boolean,
    public readonly channel: TeamMarkedDoneChannel | null,
    public readonly reportExcerpt: string | null,
  ) {}

  static fromFragment(data: MarkedDoneNodeFragment): MarkedDoneSummary {
    const report = data.report;
    return new MarkedDoneSummary(
      data.id,
      dayjs(data.markedDoneAt),
      data.client?.id ?? null,
      data.client?.name ?? null,
      data.client?.externalReference ?? null,
      isAccessibleClient(data.client),
      data.channel ?? null,
      report ? report.summary || report.content.slice(0, 200) : null,
    );
  }
}

export class TodoSummary {
  constructor(
    public readonly id: string,
    public readonly title: string,
    public readonly description: string,
    public readonly dueDate: Dayjs | null,
    public readonly createdAt: Dayjs | null,
    /** Null when the client record no longer exists. */
    public readonly clientId: string | null,
    public readonly clientName: string | null,
    public readonly clientExternalReference: string | null,
    /** False when the client is outside the viewer's own scope. */
    public readonly clientAccessible: boolean,
  ) {}

  static fromFragment(data: TodoNodeFragment): TodoSummary {
    return new TodoSummary(
      data.id,
      data.title ?? "",
      data.description ?? "",
      data.dueDate ? dayjs(data.dueDate) : null,
      data.createdAt ? dayjs(data.createdAt) : null,
      data.client?.id ?? null,
      data.client?.name ?? null,
      data.client?.externalReference ?? null,
      isAccessibleClient(data.client),
    );
  }
}

interface TeamMemberCountsFragment {
  meetings: number;
  recaps: number;
  dismissals: number;
  openTodos: number;
  markedDone?: number | null;
  meetingStates?: MeetingStateCountsFragment | null;
}

interface EmployeeEdgeFragment {
  node: {
    name: string;
    employee: {
      id: string;
      name: string;
      region?: string | null;
    };
    meetings?: {
      edges?: MeetingEdgeFragment[] | null;
    } | null;
    appointments?: {
      edges?: MeetingEdgeFragment[] | null;
    } | null;
    dismissals?: {
      edges?: DismissalEdgeFragment[] | null;
    } | null;
    markedDone?: {
      edges?: MarkedDoneEdgeFragment[] | null;
    } | null;
    counts?: TeamMemberCountsFragment | null;
  };
  revenue?: EmployeeKpiValueFragment | null;
  margin?: EmployeeKpiValueFragment | null;
  profit?: EmployeeKpiValueFragment | null;
}

interface EmployeeTotalsFragment {
  revenue?: EmployeeKpiValueFragment | null;
  margin?: EmployeeKpiValueFragment | null;
  profit?: EmployeeKpiValueFragment | null;
  activityMetrics?: ActivityMetricsFragment | null;
}

export class KpiValue {
  constructor(
    public readonly totalValue: number,
    public readonly percentageChange: number | null,
    public readonly previousTotalValue: number | null = null,
  ) {}
}

export default class TeamEmployee {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly region: string | null,
    public readonly revenue: KpiValue,
    public readonly margin: KpiValue,
    public readonly profit: KpiValue,
    public readonly activityMetrics: ActivityMetrics | null,
    public readonly meetings: MeetingSummary[] | null = null,
    public readonly dismissals: DismissalSummary[] | null = null,
    /** Null means not loaded; an empty array means loaded with no appointments. */
    public readonly appointments: MeetingSummary[] | null = null,
    /** Null means not loaded; an empty array means loaded with no markings. */
    public readonly markedDone: MarkedDoneSummary[] | null = null,
  ) {}

  static fromEdge(edge: EmployeeEdgeFragment): TeamEmployee {
    const meetings = edge.node.meetings?.edges?.map((e) => MeetingSummary.fromFragment(e.node)) ?? null;
    const appointments = edge.node.appointments?.edges?.map((e) => MeetingSummary.fromFragment(e.node)) ?? null;
    const dismissals = edge.node.dismissals?.edges?.map((e) => DismissalSummary.fromFragment(e.node)) ?? null;
    const markedDone = edge.node.markedDone?.edges?.map((e) => MarkedDoneSummary.fromFragment(e.node)) ?? null;

    return new TeamEmployee(
      edge.node.employee.id,
      edge.node.name,
      edge.node.employee.region ?? null,
      new KpiValue(
        edge.revenue?.totalValue ?? 0,
        edge.revenue?.percentageChange ?? null,
        edge.revenue?.previousTotalValue ?? null,
      ),
      new KpiValue(
        edge.margin?.totalValue ?? 0,
        edge.margin?.percentageChange ?? null,
        edge.margin?.previousTotalValue ?? null,
      ),
      new KpiValue(
        edge.profit?.totalValue ?? 0,
        edge.profit?.percentageChange ?? null,
        edge.profit?.previousTotalValue ?? null,
      ),
      edge.node.counts ? ActivityMetrics.fromCounts(edge.node.counts) : null,
      meetings,
      dismissals,
      appointments,
      markedDone,
    );
  }
}

export class TeamKpiSummary {
  constructor(
    public readonly revenue: KpiValue,
    public readonly margin: KpiValue,
    public readonly profit: KpiValue,
    public readonly activityMetrics: ActivityMetrics | null,
  ) {}

  static fromTotals(totals: EmployeeTotalsFragment): TeamKpiSummary {
    return new TeamKpiSummary(
      new KpiValue(
        totals.revenue?.totalValue ?? 0,
        totals.revenue?.percentageChange ?? null,
        totals.revenue?.previousTotalValue ?? null,
      ),
      new KpiValue(
        totals.margin?.totalValue ?? 0,
        totals.margin?.percentageChange ?? null,
        totals.margin?.previousTotalValue ?? null,
      ),
      new KpiValue(
        totals.profit?.totalValue ?? 0,
        totals.profit?.percentageChange ?? null,
        totals.profit?.previousTotalValue ?? null,
      ),
      totals.activityMetrics ? ActivityMetrics.fromGraphql(totals.activityMetrics) : null,
    );
  }
}
